import { Erc20Token__factory, Multicall__factory } from 'public/abi/evm/types';
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';
import { BigNumber } from 'bignumber.js';
import { formatEther } from 'ethers';
import useProvider from '~/composables/useProvider';
import useEnvs from './useEnvs';

interface TokenBalance {
  tokenAddress: string;
  balance: string;
  formattedBalance: string;
}

export default (tokenAddresses: Ref<string[]>) => {
  const { address } = useWeb3ModalAccount();
  const { blockchain } = useEnvs();
  const { getProvider } = useProvider();

  const tokenBalances = ref<TokenBalance[]>([]);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const fetchTokenBalances = async () => {
    if (!address.value || !tokenAddresses.value.length) {
      tokenBalances.value = [];
      return;
    }

    try {
      isLoading.value = true;
      error.value = null;

      const multicallContract = Multicall__factory.connect(blockchain.multicallAddress, getProvider());
      const erc20Interface = Erc20Token__factory.createInterface();

      const callData = tokenAddresses.value.map((tokenAddress) => {
        const encodedCall = erc20Interface.encodeFunctionData('balanceOf', [String(address.value)]);
        return [tokenAddress, encodedCall];
      });

      const multicallResult = await multicallContract?.tryAggregate.staticCall(
        true,
        callData.map((call) => ({
          target: call[0],
          callData: call[1]
        }))
      );

      tokenBalances.value = multicallResult.map((result: [boolean, string], index: number) => {
        const tokenAddress = tokenAddresses.value[index];

        if (!result[0]) {
          return {
            tokenAddress,
            balance: '0',
            formattedBalance: '0.00'
          };
        }

        const decoded = erc20Interface.decodeFunctionResult('balanceOf', result[1]);
        const balance = decoded[0].toString();
        const formattedBalance = new BigNumber(formatEther(balance)).toFixed(2, BigNumber.ROUND_FLOOR);

        return {
          tokenAddress,
          balance,
          formattedBalance
        };
      });
    } catch (err) {
      console.error('Error fetching token balances:', err);
      error.value = err instanceof Error ? err.message : 'Unknown error';
      tokenBalances.value = [];
    } finally {
      isLoading.value = false;
    }
  };

  const getTokenBalance = (tokenAddress: string): string => {
    const tokenBalance = tokenBalances.value.find((tb) => tb.tokenAddress.toLowerCase() === tokenAddress.toLowerCase());
    return tokenBalance?.formattedBalance || '0.00';
  };

  const getTokenRawBalance = (tokenAddress: string): string => {
    const tokenBalance = tokenBalances.value.find((tb) => tb.tokenAddress.toLowerCase() === tokenAddress.toLowerCase());
    return tokenBalance?.balance || '0';
  };

  watch(
    [address, tokenAddresses],
    async ([addressValue, tokenAddressesValue]) => {
      if (addressValue && tokenAddressesValue?.length) {
        await fetchTokenBalances();
      } else {
        tokenBalances.value = [];
      }
    },
    {
      immediate: true
    }
  );

  return {
    tokenBalances,
    isLoading,
    error,
    fetchTokenBalances,
    getTokenBalance,
    getTokenRawBalance
  };
};
